<template>
  <el-dialog
    v-model="visible"
    title="Создание смены"
    class="col-12 col-sm-6 col-lg-5"
    @close="resetForm"
  >
    <el-form :model="form" ref="formRef" label-width="120px">
      <el-form-item label="Сотрудник" prop="user_id" :rules="[{ required: true, message: 'Выберите сотрудника', trigger: 'change' }]">
        <el-select v-model="form.user_id" placeholder="Выберите">
          <el-option
            v-for="user in employees"
            :key="user.id"
            :label="user.name"
            :value="user.id"
          />
        </el-select>
        <el-alert
          v-if="userError"
          type="error"
          show-icon
          :closable="false"
          class="employee-error-alert"
        >
          <template #title>
            {{ userError }}
            <el-link
              type="primary"
              style="margin-left: 6px;"
              @click="$router.push('/employees')"
            >
              Перейти к настройке доступа
            </el-link>
          </template>
        </el-alert>
      </el-form-item>

      <el-form-item label="Локация" prop="location_id" :rules="[{ required: true, message: 'Выберите локацию', trigger: 'change' }]">
        <el-select v-model="form.location_id" placeholder="Выберите" :disabled="!form.user_id || !!userError">
          <el-option
            v-for="location in filteredLocations"
            :key="location.id"
            :label="location.name"
            :value="location.id"
            :disabled="isLocationUsed(location)"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="Шаблон смены">
        <div class="template-actions">
          <el-button type="primary" plain size="small" @click="applyTemplateDialogVisible = true" :disabled="!form.location_id">Применить шаблон</el-button>
        </div>
      </el-form-item>
    </el-form>
    <shift-calendar
      v-model:shifts="generatedShifts"
      mode="edit"
      :workingHours="workingHours"
      :disabled="!form.location_id"
    />

    <template #footer>
      <el-button @click="visible = false">Отмена</el-button>
      <el-button type="primary" @click="submit">Создать</el-button>
    </template>
  </el-dialog>



  <el-dialog v-model="applyTemplateDialogVisible" title="Применить шаблон" width="320">
    <el-select v-model="selectedTemplate" placeholder="Выберите шаблон" style="width: 100%; margin-bottom: 12px;">
      <el-option
        v-for="template in templates"
        :key="template.id"
        :label="template.name"
        :value="template.id"
      />
    </el-select>
    <el-date-picker
      v-model="dateRange"
      type="daterange"
      start-placeholder="Дата начала"
      end-placeholder="Дата окончания"
      :disabled-date="disabledPastDates"
      range-separator="—"
      unlink-panels
      style="width: 100%; margin-bottom: 12px;"
    />
    <el-button type="primary" @click="applyTemplate">Применить</el-button>
  </el-dialog>
</template>
<script>
import { mapActions, mapState } from 'vuex'
import ShiftCalendar from './ShiftCalendar.vue'
import { generateShiftsFromTemplate } from '@/components/util/scheduleTemplate'

export default {
  name: 'CreateScheduleDialog',
  components: {
    ShiftCalendar
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      form: {
        user_id: '',
        location_id: '',
        date: '',
        start_time: '',
        end_time: ''
      },
      userError: null,
      workingHours: null,
      generatedShifts: [],
      applyTemplateDialogVisible: false,
      selectedTemplate: null,
      dateRange: []
    }
  },
  computed: {
    ...mapState('users', ['employees']),
    ...mapState('cities', {
      locations: state => {
        const flattened = []
        state.cities.forEach(city => {
          city.children?.forEach(branch => {
            // Add branch entry with room_id: null
            flattened.push({
              id: `branch_${branch.id}`,
              name: `${city.name} / ${branch.name}`,
              city_id: city.id,
              branch_id: branch.id,
              room_id: null,
              working_times: (branch.working_times || branch.working || null)
            })
            branch.children?.forEach(room => {
              flattened.push({
                id: `room_${room.id}`,
                name: `${city.name} / ${branch.name} / ${room.name}`,
                city_id: city.id,
                branch_id: branch.id,
                room_id: room.id,
                working_times: (room.working_times || room.working || null)
              })
            })
          })
        })
        return flattened
      }
    }),
    ...mapState('schedules', {
      templates: state => state.schedulesTemplates,
      schedulesList: state => state.schedules
    }),
    visible: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('update:modelValue', val)
      }
    },
    filteredLocations() {
      if (!this.form.user_id) {
        return [];
      }
      const user = this.employees.find(u => u.id === this.form.user_id);
      if (!user || !user.locations || !user.locations.length) {
        return [];
      }
      // Collect allowed locations based on user's allowed locations
      const allowedLocations = [];
      user.locations.forEach(userLoc => {
        if (userLoc.room_id) {
          // Allow only exact room matches
          this.locations.forEach(loc => {
            if (
              loc.city_id === userLoc.city_id &&
              loc.branch_id === userLoc.branch_id &&
              loc.room_id === userLoc.room_id
            ) {
              allowedLocations.push(loc);
            }
          });
        } else if (userLoc.branch_id && userLoc.room_id === null) {
          // Allow only branch entries (room_id null, branch_id match, city_id match)
          this.locations.forEach(loc => {
            if (
              loc.city_id === userLoc.city_id &&
              loc.branch_id === userLoc.branch_id &&
              loc.room_id === null
            ) {
              allowedLocations.push(loc);
            }
          });
        }
      });
      // Remove duplicates (in case user has multiple locations that include same location)
      const uniqueLocations = [];
      const ids = new Set();
      allowedLocations.forEach(loc => {
        if (!ids.has(loc.id)) {
          ids.add(loc.id);
          uniqueLocations.push(loc);
        }
      });

      const userId = this.form.user_id;
      const schedules = this.schedulesList || [];
      const userSchedule = schedules.find(s => String(s.id) === String(userId));
      if (!userSchedule) {
        return uniqueLocations;
      }

      const userKey = `${userSchedule.city_id}_${userSchedule.branch_id}_${userSchedule.room_id ?? 0}`;

      return uniqueLocations.filter(loc => {
        const locKey = `${loc.city_id}_${loc.branch_id}_${loc.room_id ?? 0}`;
        const isUsed = locKey === userKey;
        return !isUsed;
      });
    }
  },
  methods: {
    ...mapActions('schedules', ['saveSchedule']),
    resetForm() {
      this.form = {
        user_id: '',
        location_id: '',
        date: '',
        start_time: '',
        end_time: ''
      }
      this.userError = ''
      this.workingHours = null
      this.generatedShifts = []
    },
    checkUserAccess(userId, locationId) {
      const user = this.employees.find(u => u.id === userId)
      if (!user) {
        this.$message.error('Сотрудник не найден.');
        return false;
      }
      if (!user.locations || !user.locations.length) {
        this.$message.error('Сотруднику не назначена локация. Настройте доступ перед созданием смены.');
        return false;
      }
      const selectedLocation = this.locations.find(loc => loc.id === locationId)
      if (!selectedLocation) {
        return false
      }
      for (const loc of user.locations) {
        if (loc.room_id) {
          if (
            loc.room_id === selectedLocation.room_id &&
            loc.branch_id === selectedLocation.branch_id &&
            loc.city_id === selectedLocation.city_id
          ) {
            return true
          }
        } else if (loc.branch_id) {
          if (
            loc.branch_id === selectedLocation.branch_id &&
            loc.city_id === selectedLocation.city_id
          ) {
            return true
          }
        } else if (loc.city_id) {
          this.$message.error('Сотруднику необходимо назначить офис/кабинет')
          return false
        }
      }
      return false
    },
    disabledPastDates(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date < today;
    },
    async submit() {
      if (!this.checkUserAccess(this.form.user_id, this.form.location_id)) return
      await this.$refs.formRef.validate()

      const location = this.locations.find(loc => loc.id === this.form.location_id)
      if (!location) {
        this.$message.error('Локация не найдена')
        return
      }
      if (!this.generatedShifts.length) {
        this.$message.warning('Нет смен для сохранения — примените шаблон или выделите дни в календаре')
        return
      }

      const payload = {
        user_id: this.form.user_id,
        city_id: location.city_id,
        branch_id: location.branch_id,
        room_id: location.room_id,
        shifts: this.generatedShifts.map(shift => ({
          date: shift.date,
          start_time: shift.start_time,
          end_time: shift.end_time,
          breaks: Array.isArray(shift.breaks)
            ? shift.breaks.map(b => ({
              type: b.type,
              start: b.start_time,
              end: b.end_time,
              date: shift.date
            }))
            : []
        }))
      }
      try {
        await this.saveSchedule(payload)   // bulk сохранение через стор
        this.$message.success('Смены сохранены')
        this.visible = false
        this.resetForm()
      } catch (e) {
        this.$message.error('Ошибка при сохранении смен')
      }
    },
    applyTemplate() {
      const template = this.templates.find(t => t.id === this.selectedTemplate)
      if (!template || !this.dateRange.length || !this.workingHours) {
        this.$message.error('Выберите шаблон, даты и локацию')
        return
      }
      const [startDate, endDate] = this.dateRange
      this.generatedShifts = []
      const shifts = generateShiftsFromTemplate(
        template,
        startDate,
        endDate,
        this.workingHours
      )
      this.generatedShifts = [...shifts]
      this.$message.success('Смены сгенерированы')
      this.selectedTemplate = null
      this.dateRange = []
      this.applyTemplateDialogVisible = false
    },
    isLocationUsed(location) {
      const userId = this.form.user_id;
      const existingShifts = this.$store.state.schedules.list || [];
      return existingShifts.some(
        s =>
          String(s.user_id) === String(userId) &&
          s.city_id === location.city_id &&
          s.branch_id === location.branch_id &&
          s.room_id === location.room_id
      );
    },
  },
  watch: {
    'form.user_id'(newVal) {
      if (!newVal) {
        this.userError = '';
        return;
      }
      const user = this.employees.find(u => u.id === newVal);
      if (!user || !user.locations || !user.locations.length) {
        this.userError = 'Сотруднику не назначена локация. Настройте доступ перед созданием смены.';
        return;
      }
      // Check if all locations are only city-level (no branch_id and no room_id)
      const onlyCityLevel = user.locations.every(loc => !loc.branch_id && !loc.room_id && !!loc.city_id);
      if (onlyCityLevel) {
        this.userError = 'Сотруднику необходимо назначить офис/кабинет';
        return;
      }
      this.userError = '';
    },
    'form.location_id'(newVal) {
      if (!newVal) {
        this.workingHours = null;
        return;
      }
      const location = this.locations.find(loc => loc.id === newVal);
      if (location && location.working_times) {
        this.workingHours = location.working_times;
      } else {
        this.workingHours = null;
      }
    }
  },
}
</script>
<style scoped>
.el-form-item {
  margin-bottom: 18px;
}
.employee-error-alert {
  margin-top: 8px;
}
.template-actions {
  display: flex;
  justify-content: space-between;
  width: 100%;
}
.spacer {
  flex: 1;
}
.calendar-placeholder {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 8px;
}
</style>
